import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import ProtectedRoute, { PremiumRoute } from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import MagicLink from './pages/MagicLink';
import Dashboard from './pages/Dashboard';
import ClientList from './pages/ClientList';
import CreateClient from './pages/CreateClient';
import ClientDetail from './pages/ClientDetail';
import AgentList from './pages/AgentList';
import CreateAgent from './pages/CreateAgent';
import TrainAgent from './pages/TrainAgent';
import AgentDetail from './pages/AgentDetail';
import Verification from './pages/Verification';
import UserManagement from './pages/UserManagement';
import Profile from './pages/Profile';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminOrganizations from './pages/AdminOrganizations';
import AdminOrganizationDetail from './pages/AdminOrganizationDetail';
import AdminUsers from './pages/AdminUsers';
import AdminWhatsAppAgents from './pages/AdminWhatsAppAgents';
import AdminManagement from './pages/AdminManagement';
import AdminSystemSettings from './pages/AdminSystemSettings';
import AdminPackageManagement from './pages/AdminPackageManagement';
import AdminRolesPermissions from './pages/AdminRolesPermissions';
import AdminAuditTrails from './pages/AdminAuditTrails';
import SubscriptionPage from './pages/SubscriptionPage';
import UsagePage from './pages/UsagePage';
import BillingHistoryPage from './pages/BillingHistoryPage';
import SendTextPage from './pages/SendTextPage';
import SendGroupMessagePage from './pages/SendGroupMessagePage';
import SendVoicePage from './pages/SendVoicePage';
import SendPollPage from './pages/SendPollPage';
import SendLocationPage from './pages/SendLocationPage';
import CreateChannelPage from './pages/CreateChannelPage';
import SetStatusPage from './pages/SetStatusPage';
import ViewStatusPage from './pages/ViewStatusPage';
import ConfigureForwardUrlPage from './pages/ConfigureForwardUrlPage';
import AuditTrails from './pages/AuditTrails';
import AddToolsPage from './pages/AddToolsPage';
import APIDocs from './pages/APIDocs';
import ContactSupport from './pages/ContactSupport';
import './index.css';

function App() {
  return (
    <Router>
      <AuthProvider>
        <AdminAuthProvider>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/auth/magic" element={<MagicLink />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/organizations" element={
                <AdminProtectedRoute>
                  <AdminOrganizations />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/organizations/:orgId/details" element={
                <AdminProtectedRoute>
                  <AdminOrganizationDetail />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/organizations/users" element={
                <AdminProtectedRoute>
                  <AdminUsers />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/organizations/whatsapp-agents" element={
                <AdminProtectedRoute>
                  <AdminWhatsAppAgents />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/packages" element={
                <AdminProtectedRoute>
                  <AdminPackageManagement />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/roles-permissions" element={
                <AdminProtectedRoute>
                  <AdminRolesPermissions />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/audit-trails" element={
                <AdminProtectedRoute>
                  <AdminAuditTrails />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/admins" element={
                <AdminProtectedRoute>
                  <AdminManagement />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <AdminProtectedRoute>
                  <AdminSystemSettings />
                </AdminProtectedRoute>
              } />
              
              {/* Protected Routes */}
              <Route path="/dashboard/*" element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/clients" element={<ClientList />} />
                      <Route path="/clients/create" element={<CreateClient />} />
                      <Route path="/clients/:clientId" element={<ClientDetail />} />
                      <Route path="/connections" element={<ClientList />} />
                      <Route path="/connections/:clientId" element={<ClientDetail />} />
                      <Route path="/agents" element={<AgentList />} />
                      <Route path="/agents/create" element={<CreateAgent />} />
                      <Route path="/agents/train" element={<TrainAgent />} />
                      <Route path="/agents/tools" element={<AddToolsPage />} />
                      <Route path="/agents/:id" element={<AgentDetail />} />
                      <Route path="/verification" element={<Verification />} />
                      <Route path="/users" element={<UserManagement />} />
                      <Route path="/audit-trails" element={<AuditTrails />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/docs" element={
                        <PremiumRoute feature="API Documentation" requireScale={true}>
                          <APIDocs />
                        </PremiumRoute>
                      } />
                      <Route path="/contact-support" element={<ContactSupport />} />
                      <Route path="/billing/subscription" element={<SubscriptionPage />} />
                      <Route path="/billing/usage" element={<UsagePage />} />
                      <Route path="/billing/history" element={<BillingHistoryPage />} />
                      <Route path="/requests/chat/text" element={
                        <PremiumRoute feature="Direct Text Messaging">
                          <SendTextPage />
                        </PremiumRoute>
                      } />
                      <Route path="/requests/chat/group" element={
                        <PremiumRoute feature="Group Message Sending">
                          <SendGroupMessagePage />
                        </PremiumRoute>
                      } />
                      <Route path="/requests/chat/voice" element={
                        <PremiumRoute feature="Voice Message Sending">
                          <SendVoicePage />
                        </PremiumRoute>
                      } />
                      <Route path="/requests/chat/poll" element={
                        <PremiumRoute feature="Poll Creation">
                          <SendPollPage />
                        </PremiumRoute>
                      } />
                      <Route path="/requests/chat/location" element={
                        <PremiumRoute feature="Location Sharing">
                          <SendLocationPage />
                        </PremiumRoute>
                      } />
                      <Route path="/requests/chat/channel" element={
                        <PremiumRoute feature="Channel Creation">
                          <CreateChannelPage />
                        </PremiumRoute>
                      } />
                      <Route path="/requests/forward-url" element={
                        <PremiumRoute feature="Message Forward URL">
                          <ConfigureForwardUrlPage />
                        </PremiumRoute>
                      } />
                      <Route path="/requests/status/set" element={
                        <PremiumRoute feature="Status Updates">
                          <SetStatusPage />
                        </PremiumRoute>
                      } />
                      <Route path="/requests/status/view" element={
                        <PremiumRoute feature="Status Viewing">
                          <ViewStatusPage />
                        </PremiumRoute>
                      } />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              } />
            </Routes>
          
          <Toaster
            position="top-right"
            richColors
            closeButton
            duration={4000}
            theme="dark"
          />
          </div>
        </AdminAuthProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
